// src/components/dashboard/DashboardEngagements.tsx
//
// List view of ALL of this entity's engagements — committed, verified,
// or settled. Reads GET /engagements (get_engagements under the hood),
// NOT /audit — that route only surfaces VERIFIED engagements
// (get_track_record's trackRecordEntries are written exclusively at
// verification), which meant a merely-committed engagement was
// previously invisible from this list view entirely, reachable only by
// navigating directly to a known engagementRef. This fixes that.
//
// UPDATED — cards now show outcomeName, scope, and counterpartyLabel
// instead of the bare engagementRef. A raw ref like
// "eng_c83baff042a153e446f24ec9" tells a merchant nothing about what
// the engagement actually is — get-engagements.ts now resolves and
// returns the outcome's real name, the commit-time scope summary, and
// a human-readable counterparty label (same resolution pattern
// get-message-threads.ts already established), so this list reads like
// "Marqeta auth/clearing anomaly detection — chatgpt+counter@..." rather
// than an opaque hash.

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { authedFetch } from '../../services/dashboardApi';
import { CheckCircle2Icon, ClockIcon } from 'lucide-react';

interface EngagementSummary {
  engagementRef: string;
  outcomeId: string | null;
  outcomeName: string;
  counterpartyId: string;
  counterpartyLabel: string | null;
  scope: string;
  status: 'committed' | 'verified' | 'settled';
  agreedValueCents: number;
  committedAt: string;
  verifiedAt: string | null;
}

interface EngagementsResponse {
  engagements: EngagementSummary[];
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function formatCents(cents: number): string {
  return `$${(cents / 100).toLocaleString()}`;
}

function statusBadge(status: EngagementSummary['status']) {
  if (status === 'committed') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-700">
        <ClockIcon className="size-3.5" /> Committed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-2 py-0.5 text-xs text-green-700">
      <CheckCircle2Icon className="size-3.5" /> {status === 'settled' ? 'Settled' : 'Verified'}
    </span>
  );
}

export const DashboardEngagements: React.FC = () => {
  const [engagements, setEngagements] = useState<EngagementSummary[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await authedFetch('/engagements');
        if (!res.ok) throw new Error('Failed to load engagements.');
        const data: EngagementsResponse = await res.json();
        setEngagements(data.engagements);
      } catch (err: any) {
        setError(err.message || 'Could not load engagements.');
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  if (isLoading) return <p className="text-sm text-brand-gray font-mono">Loading…</p>;
  if (error) return <p className="text-sm text-red-600">{error}</p>;

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold text-brand-black">Engagements</h1>

      {!engagements || engagements.length === 0 ? (
        <p className="text-sm text-brand-gray">No engagements yet.</p>
      ) : (
        <div className="space-y-2">
          {engagements.map((eng) => (
            <Link
              key={eng.engagementRef}
              to={`/dashboard/engagements/${eng.engagementRef}`}
              className="block rounded-xl border border-brand-border p-4 hover:bg-brand-surface transition-colors"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-brand-black">{eng.outcomeName}</span>
                {statusBadge(eng.status)}
              </div>
              {eng.scope && (
                <p className="mt-1 text-xs text-brand-gray line-clamp-1">{eng.scope}</p>
              )}
              <p className="mt-1 text-xs text-brand-gray">
                {eng.counterpartyLabel ?? eng.counterpartyId} · {formatCents(eng.agreedValueCents)} · Committed {formatDate(eng.committedAt)}
              </p>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};